import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import {
  ConfirmationModal,
  EmptyStateBase,
  Page,
  PaginationBlock,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Table,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useScoreCardDelete,
  useScoreCardDuplicate,
  useScoreCardSave,
  useScoreCardSearch,
} from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiScoreCardSearchFilter, ApiScoreCardShort } from "@/types/recruit/scorecard.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export function ScoreCardsIndex() {
  const { page_number, text } = filtersLibrary();
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const navigate = useNavigate();
  const { openModal } = useModals();
  useTitle(t("scorecards:scorecards"), t("ats:recruit"));

  const {
    filter: { filter },
  } = useAppFilter<ApiScoreCardSearchFilter>({
    filters: [page_number, text],
    source: "ScoreCards",
  });

  const { data: scoreCards, isLoading, isFetching } = useScoreCardSearch(filter);
  const scoreCardCreate = useScoreCardSave();
  const scoreCardDelete = useScoreCardDelete();
  const scoreCardDuplicate = useScoreCardDuplicate();
  const [searchTerm, setSearchTerm] = useState("");
  const [, setSearchParams] = useSearchParams();

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const columns = useMemo<ColumnDef<ApiScoreCardShort>[]>(() => {
    const inner: ColumnDef<ApiScoreCardShort>[] = [
      {
        header: t("scorecards:scorecard"),
        cell: (row) => (
          <ListItem
            emoji={row.row.original.emoji}
            title={row.row.original.title}
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ];

    return inner;
  }, [scoreCards]);

  const handleScoreCardsCreate = async () => {
    try {
      const data = await scoreCardCreate.mutateAsync({});

      analyticsService.trackEvent(analyticEvents.scoreCards.created, {
        [analyticProperties.id]: data.id,
      });

      navigate(`/recruitment/scorecards/${data.id}/setup/details`);
    } catch {
      //
    }
  };

  const handleDelete = useCallback(
    async (id: number) => {
      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async function () {
          try {
            const result = await scoreCardDelete.mutateAsync(id);

            if (!result.success) return;

            analyticsService.trackEvent(analyticEvents.scoreCards.deleted, {
              [analyticProperties.id]: id,
            });
          } catch {
            //
          }
        },
      });
    },
    [openModal, scoreCardDelete, t]
  );

  const handleDuplicate = useCallback(
    (id: number) => {
      openModal(ConfirmationModal, {
        question: t("scorecards:duplicate_scorecard"),
        text: t("scorecards:duplicate_scorecard_confirm"),
        acceptTitle: t("common:duplicate"),
        onAccept: async function () {
          try {
            const data = await scoreCardDuplicate.mutateAsync({ id });

            analyticsService.trackEvent(analyticEvents.scoreCards.duplicated, {
              [analyticProperties.id]: id,
            });

            navigate(`/recruitment/scorecards/${data.id}/setup/details`);
          } catch {
            //
          }
        },
      });
    },
    [openModal, scoreCardDuplicate, navigate, t]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiScoreCardShort>) => {
      if (!canIEdit(ApiPermission.ats_manage_scorecards)) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            to={`/recruitment/scorecards/${row.original.id}/setup/details`}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => handleDuplicate(row.original.id)}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
            title={t("scorecards:duplicate_scorecard") + "..."}
          />
          <DropdownMenuItem
            onClick={() => handleDelete(row.original.id)}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
            variant={"menuDanger"}
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [canIEdit, handleDelete, handleDuplicate, t]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.scoreCards.viewed);
  });

  return (
    <Page className="h-full">
      <Page.Header title={t("scorecards:scorecards")}>
        {canIEdit(ApiPermission.ats_manage_scorecards) && (
          <Button isLoading={scoreCardCreate.isPending} onClick={handleScoreCardsCreate}>
            {t("scorecards:create_scorecard")}
          </Button>
        )}
      </Page.Header>

      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <Stack direction={"horizontal"}>
              <Search
                value={searchTerm}
                onInput={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
                type={"text"}
                placeholder={t("common:search_by_name")}
                className={"w-96"}
              />
            </Stack>
          }
          footer={
            <PaginationBlock
              metadata={scoreCards?.metadata}
              onPageChange={(page) => {
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                });
              }}
            />
          }
        >
          <Table
            stickyHeader={true}
            renderRowAction={renderRowAction}
            data={scoreCards?.items || []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isLoading || isFetching}
            showNotFoundError={filter.text != undefined && scoreCards?.metadata.total_items == 0}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={
              <EmptyStateBase
                emoji={":raising_hand:"}
                title={t("scorecards:create_new_motivation_title")}
                description={t("scorecards:create_new_motivation_description")}
                primaryButton={
                  canIEdit(ApiPermission.ats_manage_scorecards) && (
                    <Button isLoading={scoreCardCreate.isPending} onClick={handleScoreCardsCreate}>
                      {t("scorecards:create_scorecard")}
                    </Button>
                  )
                }
              />
            }
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
